import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'

export const getRandomInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min) + min)

export const getRandomBoolean = (): boolean => Math.random() < 0.5

export const getUniqueRandomIntArray = (
  count: number,
  min: number,
  max: number
): number[] => {
  const uniqueInts = new Set<number>()
  while (uniqueInts.size < count) {
    uniqueInts.add(getRandomInt(min, max))
  }

  return Array.from(uniqueInts)
}

export const getRandomFromArray = <T>(arr: readonly T[] | T[]): T =>
  arr[getRandomInt(0, arr.length)]

type Options<T> = {
  itemFactory: (paginatedIndex: number) => T
  page?: number
  page_size?: number | 'max'
}

type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
  page_size: number
  current: number
}

export const createPaginatedResponseMock = <T>(
  options: Options<T>
): PaginatedResponse<T> => {
  const count = 120
  const page_size =
    options.page_size === 'max' ? count : options.page_size || DEFAULT_PAGE_SIZE

  const MAX_PAGE = Math.ceil(count / page_size)
  const page = options.page || 1

  const current = page > MAX_PAGE ? MAX_PAGE : page
  const next = current < MAX_PAGE ? `?page=${current + 1}` : null
  const previous = current > 0 ? `?page=${current + 1}` : null

  const getPaginatedIndex = (index: number): number =>
    options.page_size === 'max' ? index : index + (current - 1) * page_size

  return {
    count,
    current,
    page_size,
    next,
    previous,
    results: Array.from({ length: page_size }, (_, index) =>
      options.itemFactory(getPaginatedIndex(index))
    )
  }
}

export const mockQuery = <T>(
  data: T,
  timeout = 2000
): Promise<{ results: unknown }> =>
  new Promise(resolve => {
    setTimeout(() => {
      resolve({ results: data })
    }, timeout)
  })

export const mockPaginatedQuery = <T extends PaginatedResponse<unknown>>(
  data: T,
  timeout = 2000
): Promise<T> =>
  new Promise(resolve => {
    setTimeout(() => {
      resolve(data)
    }, timeout)
  })
